import { SiteDirection } from '@wix/a11y-adapters';
import { domOrderRule, RuleId } from '@wix/a11y-rules';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus } from '@wix/rules-panel-ui';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../../atoms/bi-atoms';
import { BiCtaNames } from '../../../bi/types';
import { convertScanStatusToUiStatus } from '../../../components/fixers/alt-image-fixer/utils';
import { RuleDescriptionComponent } from '../../../components/rule-description-component';
import { SITE_LEVEL_PAGE_ID } from '../../../consts';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../../hooks/use-open-article-by-editor-host';
import { isClassicEditorHost } from '../../../utils/get-editor-host';
import { getFixerDesignProps, getFooterButtonTypes } from '../../../utils/get-fixer-design-props';
import { useRuleBase } from '../../shared/use-rule-base';
import { getUpdateDomOrderRuleStateOnApply } from './get-update-dom-order-rule-state-on-apply';
import { getRadioOptions } from './radio-options';
import { useDomOrderOnApply } from './use-dom-order-on-apply';
export const DOM_ORDER_FIX_OPTION = {
  AUTO: 'auto',
  MANUAL: 'manual'
};
const ruleChecker = domOrderRule.checker;
export const useDomOrderRule = () => {
  const {
    experiments
  } = useExperiments();
  const enableRTLDomOrder = experiments.enabled('specs.responsive-editor.EnableRTLDomOrder');
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    t
  } = useTranslation();
  const {
    editorHost,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    getOnApply
  } = useDomOrderOnApply({
    setIsApplyTriggered
  });
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const isLanguageRTL = a11yWizardAdapter.getIsSiteLanguageRTL();
  const isSiteDirectionMatchingLanguageDirection = a11yWizardAdapter.getIsSiteDirectionMatchingLanguageDirection();
  const getUpdateRuleStateOnApply = useCallback(siteCreationDate => {
    return fixOption => {
      const isDomOrderAutoFix = fixOption.value === DOM_ORDER_FIX_OPTION.AUTO;
      const updatedRuleContext = {
        automaticDomOrderEnabled: isDomOrderAutoFix,
        isAutomaticallyCompleted: isDomOrderAutoFix,
        isManuallyCompleted: !isDomOrderAutoFix,
        siteDomDirection: isSiteDirectionMatchingLanguageDirection ? isLanguageRTL ? SiteDirection.RTL : SiteDirection.LTR : isLanguageRTL ? SiteDirection.LTR : SiteDirection.RTL,
        siteLanguage: ''
      };
      const updatedStatus = convertScanStatusToUiStatus(ruleChecker({
        ...updatedRuleContext,
        siteCreationDate
      }).status);
      updateRuleStateAndVerifyIfNeeded(SITE_LEVEL_PAGE_ID, RuleId.DOM_ORDER, {
        ...updatedRuleContext,
        status: updatedStatus
      });
    };
  }, [isLanguageRTL, isSiteDirectionMatchingLanguageDirection, updateRuleStateAndVerifyIfNeeded]);
  const getDomOrderProps = useCallback(_ref => {
    var _getRuleState, _ruleState$state, _ruleState$state2;
    let {
      ruleExecutionResult
    } = _ref;
    const {
      result,
      context,
      ruleId
    } = ruleExecutionResult;
    const {
      status
    } = result;
    const {
      isManuallyCompleted,
      siteCreationDate,
      siteLanguage,
      isAutomaticallyCompleted
    } = context;
    const updatedSiteLang = ((_getRuleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId)) == null || (_getRuleState = _getRuleState.state) == null ? void 0 : _getRuleState.siteLanguage) ?? siteLanguage;
    const ruleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId);
    const isManuallyCompletedUIValue = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.isManuallyCompleted) ?? isManuallyCompleted;
    const uiStatus = (ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.status) ?? convertScanStatusToUiStatus(status);
    if (status === 'not-applicable') {
      return null;
    }
    let updateRuleStateOnApply;
    if (enableRTLDomOrder) {
      updateRuleStateOnApply = getUpdateDomOrderRuleStateOnApply(siteCreationDate, updatedSiteLang, updateRuleStateAndVerifyIfNeeded, a11yWizardAdapter, experiments);
    } else {
      updateRuleStateOnApply = getUpdateRuleStateOnApply(siteCreationDate);
    }
    const onApply = getOnApply({
      isManuallyCompleted: isManuallyCompletedUIValue,
      uiStatus,
      updateRuleState: updateRuleStateOnApply
    });
    const radioOptions = getRadioOptions(t, editorHost);
    const radioGroupLabel = t('accessibility-wizard.rules-list.dom-order.organize-dom-order');
    const completedRuleContentValueManually = t('accessibility-wizard.rules-list.dom-order.input-value');
    const completedRuleContentValueAuto = t('accessibility-wizard.rules-list.dom-order.input-value.automatically');
    const isEditable = isClassicEditorHost(editorHost) || isManuallyCompletedUIValue;
    const editorCompletedValue = isManuallyCompletedUIValue ? completedRuleContentValueManually : completedRuleContentValueAuto;
    const completedRuleValue = isClassicEditorHost(editorHost) ? editorCompletedValue : completedRuleContentValueManually;
    const completedRuleContent = {
      editButtonLabel: t('accessibility-wizard.common.edit'),
      inputTitle: radioGroupLabel,
      title: t('accessibility-wizard.rules-list.issue-resolved'),
      value: completedRuleValue
    };
    const defaultValueForClassic = isManuallyCompletedUIValue ? radioOptions[1] : isAutomaticallyCompleted ? radioOptions[0] : undefined;
    const defaultValue = isClassicEditorHost(editorHost) ? defaultValueForClassic : isManuallyCompletedUIValue ? radioOptions[1] : undefined;
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: isManuallyCompletedUIValue ? 'accessibility-wizard.rules-list.dom-order.description.manually-completed' : uiStatus !== RuleListStatus.failed ? 'accessibility-wizard.rules-list.dom-order.description' : enableRTLDomOrder && isLanguageRTL ? 'accessibility-wizard.rules-list.dom-order.RTL-description.failed' : 'accessibility-wizard.rules-list.dom-order.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-organizing-DOM-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(status === 'failed')
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.dom-order.description.learn-more.aria-label')
      }),
      fixer: FixerType.RadioGroupRule,
      fixerProps: {
        completedRuleContent,
        defaultValue: uiStatus === RuleListStatus.passed ? defaultValue : undefined,
        design: getFixerDesignProps(editorHost),
        footerLabels: {
          buttonTypes: getFooterButtonTypes(editorHost),
          dismiss: t('accessibility-wizard.rules-list.dom-order.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.dom-order.footer-button.apply')
        },
        onApply,
        onSelect: selectedValue => {
          biLogger.elementFieldChange({
            index: 0,
            status: uiStatus,
            value: `${selectedValue}`
          });
        },
        radioGroupLabel,
        radioOptions
      },
      id: ruleId,
      isEditableOnceCompleted: isEditable,
      label: t('accessibility-wizard.rules-list.dom-order.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: uiStatus
        });
        biLogger.elementStatusChange({
          index: 0,
          status: uiStatus
        });
      },
      parentGroupId: SITE_LEVEL_PAGE_ID,
      ruleId,
      status: uiStatus
    };
  }, [a11yWizardAdapter, biLogger, currentTaskInfo, editorHost, enableRTLDomOrder, experiments, getOnApply, getRuleState, getUpdateRuleStateOnApply, isLanguageRTL, openSelectedArticle, setCurrentTaskInfo, t, updateRuleStateAndVerifyIfNeeded]);
  return {
    getDomOrderProps
  };
};
//# sourceMappingURL=use-dom-order-rule.js.map